import type { ReactNode } from 'react';
import Box from '@/components/Unistyles/Box';
import Button from './Button';
import LoadingPlaceholder from './LoadingPlaceholder';
import Text from './Text';

export type ContentBoundaryProps<T> = {
  data: T | null | undefined;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  errorMessage?: string;
  empty?: ReactNode;
  flex?: boolean;
  loadingLabel?: string;
  loadingTestID?: string;
  children: (data: NonNullable<T>) => ReactNode;
};

export default function ContentBoundary<T>({
  data,
  loading = false,
  error,
  onRetry,
  errorMessage = 'Could not load content. Please try again.',
  empty = null,
  flex = true,
  loadingLabel,
  loadingTestID,
  children,
}: ContentBoundaryProps<T>) {
  if (data !== null && data !== undefined) return children(data);
  if (loading) {
    return (
      <LoadingPlaceholder
        flex={flex}
        accessibilityLabel={loadingLabel}
        testID={loadingTestID}
      />
    );
  }
  if (error) {
    return (
      <Box flex={flex} center p="lg" space="md">
        <Text role="alert" center>
          {errorMessage}
        </Text>
        {onRetry && (
          <Button variant="secondary" onPress={onRetry}>
            <Button.Text>Try again</Button.Text>
          </Button>
        )}
      </Box>
    );
  }
  return empty;
}
